import { z } from 'zod';
import { DAILY_DEVICE_SELECTION_SCHEMA_VERSION, WIZARD_STEP_IDS, WORLD_DOMAIN } from '../constants.js';
import { validateOperatingSchedule } from './schedule.js';
import { stripForbiddenPersistenceKeys } from './persistenceGuard.js';
import {
  CHARACTER_DRAFT_SCHEMA_VERSION, CONDITIONAL_ANSWER_SCHEMA_VERSION, OPERATING_SCHEDULE_SCHEMA_VERSION,
  PLOT_DEVICE_SCHEMA_VERSION, PROMPT_MANIFEST_SCHEMA_VERSION, SUMMARY_RECORD_SCHEMA_VERSION,
  WORLD_DRAFT_SCHEMA_VERSION, WORLD_SNAPSHOT_SCHEMA_VERSION, type CharacterDraft, type ConditionalAnswer,
  type ConnectedAnswers, type LocationGrounding, type OperatingSchedule, type TimePeriod, type WorldDraft,
  isNonBlankValue,
} from './types.js';

const isoWeekday = z.number().int().min(1).max(7);

const timeRe = /^([01]\d|2[0-3]):([0-5]\d)$/;

const answerSource = z.enum(['user', 'assumption', 'fallback', 'ai_suggestion']);

const conditionalAnswerBaseSchema = z.object({
  questionId: z.string().min(1),
  schemaVersion: z.literal(CONDITIONAL_ANSWER_SCHEMA_VERSION),
  value: z.unknown(),
  status: z.enum(['unanswered', 'answered', 'suggested', 'skipped', 'stale', 'confirmed']),
  source: answerSource.nullable(),
  dependencies: z.array(z.string()),
  updatedAt: z.string().min(1),
  validationError: z.string().optional(),
});

function refineConditionalAnswer(
  answer: z.infer<typeof conditionalAnswerBaseSchema>,
  ctx: z.RefinementCtx,
): void {
  if (answer.status === 'unanswered' && answer.value !== null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'unanswered status requires null value',
      path: ['value'],
    });
  }
  if (answer.status === 'skipped') {
    const val = answer.value as { skipped?: boolean } | null;
    if (!val || val.skipped !== true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'skipped status requires { skipped: true } value',
        path: ['value'],
      });
    }
  }
  const filled = answer.status === 'answered' || answer.status === 'suggested' || answer.status === 'confirmed';
  if (filled && !isNonBlankValue(answer.value)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: `filled status "${answer.status}" requires non-blank value`, path: ['value'] });
  }
  if (filled && answer.source === null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'filled status requires source', path: ['source'] });
  }
}

export const conditionalAnswerSchema = conditionalAnswerBaseSchema.superRefine(
  refineConditionalAnswer,
);

export const confirmedAnswerSchema = conditionalAnswerBaseSchema
  .extend({
    status: z.literal('confirmed'),
    source: answerSource,
  })
  .superRefine(refineConditionalAnswer);

const trimmedNonEmpty = z
  .string()
  .min(1)
  .refine((s) => s.trim().length > 0, { message: 'must not be blank or whitespace-only' });

export const locationGroundingSchema = z.object({
  name: trimmedNonEmpty,
  region: trimmedNonEmpty,
  language: trimmedNonEmpty,
});

const crowdTypeSchema = z.enum([
  'public_venue',
  'invited_private_allowed',
  'members_owners_workers_students_faculty',
  'other',
]);

export const connectedCrowdSchema = z
  .object({
    type: crowdTypeSchema,
    otherDescription: z.string().optional(),
  })
  .superRefine((crowd, ctx) => {
    if (crowd.type === 'other' && !crowd.otherDescription?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'crowd type "other" requires otherDescription',
        path: ['otherDescription'],
      });
    }
  });

const rhythmTypeSchema = z.enum([
  'steady_busy_all_day',
  'busy_then_slow',
  'seasonal_or_event_driven',
  'family_or_friend_driven',
  'other',
]);

export const rhythmAnswerSchema = z
  .union([
    z.object({ skipped: z.literal(true) }),
    z.object({
      skipped: z.literal(false),
      type: rhythmTypeSchema,
      otherDescription: z.string().optional(),
    }),
  ])
  .superRefine((rhythm, ctx) => {
    if (
      !rhythm.skipped &&
      rhythm.type === 'other' &&
      !rhythm.otherDescription?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'rhythm type "other" requires otherDescription',
        path: ['otherDescription'],
      });
    }
  });

export const connectedAnswersSchema = z.object({
  locationType: trimmedNonEmpty,
  description: trimmedNonEmpty,
  crowd: connectedCrowdSchema,
  rhythm: rhythmAnswerSchema,
});

function hasNonWhitespace(s: string | undefined): boolean {
  return typeof s === 'string' && s.trim().length > 0;
}

function isPunctuationOnly(s: string): boolean {
  return s.trim().length > 0 && /^[\p{P}\p{S}\s]+$/u.test(s.trim());
}

export const timePeriodSchema = z
  .object({
    present: z.string().optional(),
    period: z.string().optional(),
    rationale: z.string().optional(),
  })
  .superRefine((period, ctx) => {
    const present = period.present?.trim() ?? '';
    const era = period.period?.trim() ?? '';
    if (!hasNonWhitespace(present) && !hasNonWhitespace(era)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one of present or period is required',
      });
    }
    if (present && isPunctuationOnly(present)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'present must not be punctuation-only',
        path: ['present'],
      });
    }
    if (era && isPunctuationOnly(era)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'period must not be punctuation-only',
        path: ['period'],
      });
    }
  });

const operatingScheduleBaseSchema = z.object({
  schemaVersion: z.literal(OPERATING_SCHEDULE_SCHEMA_VERSION),
  hasOperatingHours: z.boolean(),
  timezoneStrategy: z.enum(['device', 'explicit', 'location']),
  timezone: z.string().optional(),
  daysOpen: z.array(isoWeekday),
  openTime: z.string().regex(timeRe),
  closeTime: z.string().regex(timeRe),
  overnight: z.boolean(),
  closedBehavior: z.enum(['leave', 'sleep', 'observable']),
});

export const operatingScheduleSchema = operatingScheduleBaseSchema.superRefine((schedule, ctx) => {
  if (schedule.timezoneStrategy === 'explicit' && !schedule.timezone?.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Explicit timezone strategy requires timezone' });
  }
  // Zod validates weekday range 1–7; cast to domain IsoWeekday[] for schedule validator.
  for (const message of validateOperatingSchedule(schedule as OperatingSchedule).errors) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message });
  }
});

const characterBasicSchema = z.object({
  firstName: trimmedNonEmpty,
  lastInitial: z
    .string()
    .max(1)
    .regex(/^[A-Za-z]?$/)
    .optional(),
});

const natureSlider = z.number().int().min(WORLD_DOMAIN.natureSliderMin).max(WORLD_DOMAIN.natureSliderMax);
const characterNatureSchema = z.object({
  sociability: natureSlider, agreeableness: natureSlider, emotionality: natureSlider, caution: natureSlider,
  discipline: natureSlider, dominance: natureSlider, empathy: natureSlider, curiosity: natureSlider,
  drive: natureSlider, sexual_drive: natureSlider, casual_sex_openness: natureSlider,
});
const characterAppearanceSchema = z.object({
  skinColor: z.string().min(1), topStyle: z.string().min(1), topColor: z.string().min(1),
  bottomStyle: z.string().min(1), bottomColor: z.string().min(1), hairStyle: z.string().min(1), hairColor: z.string().min(1),
});

const learnedTraitSchema = z.object({
  traitKey: z.string().min(1),
  strength: z.number().int().min(0).max(10),
  reason: z.string().max(WORLD_DOMAIN.maxTraitReasonChars),
});

const visitWindowSchema = z.object({
  n: z.number().int().min(0).max(168),
  unit: z.enum(['minutes', 'hours', 'days']),
});

const characterVisitSchema = z.object({
  visitId: z.string().min(1),
  startAt: z.string(),
  startWindow: visitWindowSchema,
  endAt: z.string(),
  endWindow: visitWindowSchema,
  reason: z.string().max(500),
  rolledArrivalAt: z.string().optional(),
  rolledDepartureAt: z.string().optional(),
}).superRefine((visit, ctx) => {
  if (visit.startAt && visit.endAt) {
    const start = new Date(visit.startAt).getTime();
    const end = new Date(visit.endAt).getTime();
    if (Number.isFinite(start) && Number.isFinite(end) && end < start) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'endAt must be on or after startAt',
        path: ['endAt'],
      });
    }
  }
});

export const characterDraftSchema = z
  .object({
    draftCharacterId: z.string().min(1),
    schemaVersion: z.literal(CHARACTER_DRAFT_SCHEMA_VERSION),
    basic: characterBasicSchema,
    sex: z.enum(['M', 'F']).optional(),
    species: z.string().min(1).optional(),
    background: z.string().max(500).optional(),
    ageRange: z.string().min(1).optional(),
    appearance: characterAppearanceSchema.nullable(),
    nature: characterNatureSchema.nullable(),
    traits: z.array(learnedTraitSchema).max(WORLD_DOMAIN.maxLearnedTraitsPerCharacter),
    status: z.enum(['draft', 'confirmed', 'removed']),
    source: answerSource,
    dependencyIds: z.array(z.string()),
    updatedAt: z.string().min(1),
    visits: z.array(characterVisitSchema).optional(),
    visitor: z.boolean().optional(),
  })
  .superRefine((character, ctx) => {
    if (character.status === 'confirmed' && (character.appearance === null || character.nature === null)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'confirmed characters require appearance and nature' });
    }
  });

export const plotDeviceSchema = z.object({
  deviceId: z.string().min(1),
  schemaVersion: z.literal(PLOT_DEVICE_SCHEMA_VERSION),
  category: z.string().min(1),
  title: z.string().min(1),
  description: z.string().min(1),
  trigger: z.string().optional(),
  consequenceRange: z.string().optional(),
  intensity: z.enum(['mild', 'moderate', 'intense']),
  source: z.enum(['seeded', 'generated']),
  contextSourceIds: z.array(z.string()).optional(),
  dependencyIds: z.array(z.string()).optional(),
  safetyStatus: z.enum(['reviewed', 'pending']).optional(),
});

export const worldClockSchema = z.object({
  inWorldIso: z.string().min(1),
  episodeNumber: z.number().int().nonnegative(),
});

export const dailyDeviceSelectionSchema = z.object({
  schemaVersion: z.literal(DAILY_DEVICE_SELECTION_SCHEMA_VERSION),
  worldId: z.string().min(1),
  inWorldDate: z.string().min(1),
  selectionSeed: z.string().min(1),
  deviceIds: z.array(z.string().min(1)).max(2),
  selectedAt: z.string().min(1),
});

export const summaryRecordSchema = z.object({
  schemaVersion: z.literal(SUMMARY_RECORD_SCHEMA_VERSION),
  summaryId: z.string().min(1),
  worldId: z.string().min(1),
  scope: z.enum(['location', 'character', 'missed_activity']),
  characterId: z.string().optional(),
  eventIds: z.array(z.string()),
  observedFacts: z.string().min(1),
  directorInterpretation: z.string().optional(),
  priorSummaryHash: z.string().optional(),
  dependencyIds: z.array(z.string()).optional(),
  createdAt: z.string().min(1),
});

export const historyEventRecordSchema = z.object({
  eventId: z.string().min(1),
  worldId: z.string().min(1),
  occurredAt: z.string().min(1),
  summary: z.string().min(1),
  actorIds: z.array(z.string()),
  deviceIds: z.array(z.string()),
  observed: z.boolean(),
});

export const promptManifestSchema = z.object({
  schemaVersion: z.literal(PROMPT_MANIFEST_SCHEMA_VERSION),
  manifestId: z.string().min(1),
  purpose: z.string().min(1),
  provider: z.enum(['deepseek', 'gemini', 'fallback']),
  fallback: z.boolean(),
  revision: z.number().int().nonnegative(),
  answerIds: z.array(z.string()),
  actorIds: z.array(z.string()),
  eventIds: z.array(z.string()),
  dependencyIds: z.array(z.string()),
  validationRule: z.string().optional(),
  createdAt: z.string().min(1),
});

const characterBondSchema = z.object({
  fromId: z.string().min(1),
  toId: z.string().min(1),
  kind: z.string().min(1),
  closeness: z.number().min(0).max(10),
});

export const worldDraftSchema = z.object({
  schemaVersion: z.literal(WORLD_DRAFT_SCHEMA_VERSION),
  draftId: z.string().min(1),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
  wizardStep: z.enum(WIZARD_STEP_IDS),
  lastVisitedStep: z.enum(WIZARD_STEP_IDS).optional(),
  location: locationGroundingSchema.nullable(),
  connected: connectedAnswersSchema.nullable(),
  answers: z.record(conditionalAnswerSchema),
  timePeriod: timePeriodSchema.nullable(),
  schedule: operatingScheduleSchema.nullable(),
  characterDrafts: z.array(characterDraftSchema),
  characterBonds: z.array(characterBondSchema).optional(),
  reviewAccepted: z.boolean(),
});

export const worldSnapshotSchema = z
  .object({
    schemaVersion: z.literal(WORLD_SNAPSHOT_SCHEMA_VERSION),
    worldId: z.string().min(1),
    version: z.number().int().positive(),
    acceptedAt: z.string().min(1),
    draftId: z.string().min(1),
    location: locationGroundingSchema,
    connected: connectedAnswersSchema,
    answers: z.record(confirmedAnswerSchema),
    timePeriod: timePeriodSchema,
    schedule: operatingScheduleSchema,
    characters: z.array(characterDraftSchema),
    characterBonds: z.array(characterBondSchema).optional(),
    devices: z.array(plotDeviceSchema),
    clock: worldClockSchema,
    operatingStatus: z.enum(['open', 'closed']),
    generationContext: z
      .object({
        worldId: z.string().min(1),
        worldVersion: z.number().int().positive(),
        answerIds: z.array(z.string()),
        generationSeed: z.string().min(1),
        generatedAt: z.string().min(1),
      })
      .optional(),
  })
  .superRefine((snapshot, ctx) => {
    for (const [questionId, answer] of Object.entries(snapshot.answers)) {
      if (answer.status !== 'confirmed') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Snapshot answer "${questionId}" must be confirmed (got ${answer.status})`,
          path: ['answers', questionId, 'status'],
        });
      }
    }
    snapshot.characters.forEach((character, index) => {
      if (character.appearance === null || character.nature === null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'snapshot characters require appearance and nature', path: ['characters', index] });
      }
    });
  });

export type ParseResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

function parseWithSchema<T>(
  schema: z.ZodType<T>,
  raw: unknown,
): ParseResult<T> {
  const cleaned = stripForbiddenPersistenceKeys(raw);
  const parsed = schema.safeParse(cleaned);
  if (!parsed.success) {
    return { ok: false, error: parsed.error.message };
  }
  return { ok: true, value: parsed.data };
}

export const parseConditionalAnswer = (raw: unknown): ParseResult<ConditionalAnswer> => {
  const parsed = parseWithSchema(conditionalAnswerSchema, raw);
  if (!parsed.ok) return parsed;
  // Zod infers a flat object; domain ConditionalAnswer is a discriminated union on status.
  return { ok: true, value: parsed.value as ConditionalAnswer };
};
export const parseConnectedAnswers = (raw: unknown): ParseResult<ConnectedAnswers> =>
  parseWithSchema(connectedAnswersSchema, raw);
export const parseLocationGrounding = (raw: unknown): ParseResult<LocationGrounding> =>
  parseWithSchema(locationGroundingSchema, raw);
export const parseTimePeriod = (raw: unknown): ParseResult<TimePeriod> =>
  parseWithSchema(timePeriodSchema, raw);
export const parseCharacterDraft = (raw: unknown): ParseResult<CharacterDraft> =>
  parseWithSchema(characterDraftSchema, raw);
export const parseOperatingSchedule = (raw: unknown): ParseResult<z.infer<typeof operatingScheduleSchema>> =>
  parseWithSchema(operatingScheduleSchema, raw);
export const parsePlotDevice = (raw: unknown): ParseResult<z.infer<typeof plotDeviceSchema>> =>
  parseWithSchema(plotDeviceSchema, raw);
export const parseDailyDeviceSelection = (raw: unknown): ParseResult<z.infer<typeof dailyDeviceSelectionSchema>> =>
  parseWithSchema(dailyDeviceSelectionSchema, raw);
export const parseSummaryRecord = (raw: unknown): ParseResult<z.infer<typeof summaryRecordSchema>> =>
  parseWithSchema(summaryRecordSchema, raw);
export const parseHistoryEventRecord = (raw: unknown): ParseResult<z.infer<typeof historyEventRecordSchema>> =>
  parseWithSchema(historyEventRecordSchema, raw);
export const parsePromptManifest = (raw: unknown): ParseResult<z.infer<typeof promptManifestSchema>> =>
  parseWithSchema(promptManifestSchema, raw);
export const parseWorldClock = (raw: unknown): ParseResult<z.infer<typeof worldClockSchema>> =>
  parseWithSchema(worldClockSchema, raw);
export const parseWorldDraft = (raw: unknown): ParseResult<WorldDraft> => {
  const parsed = parseWithSchema(worldDraftSchema, raw);
  if (!parsed.ok) return parsed;
  // Zod output matches runtime shape; cast to domain WorldDraft (rhythm + answer unions).
  return { ok: true, value: parsed.value as WorldDraft };
};
export const parseWorldSnapshot = (raw: unknown): ParseResult<z.infer<typeof worldSnapshotSchema>> =>
  parseWithSchema(worldSnapshotSchema, raw);

/** Serialize domain record to JSON string after stripping forbidden keys. */
export function serializeDomainJson(value: unknown): string {
  return JSON.stringify(stripForbiddenPersistenceKeys(value));
}

export { containsForbiddenPersistenceKeys } from './persistenceGuard.js';
export { applyAiFillPatches } from './types.js';
